// Thin wrapper around sonner that gives us:
//   1. Human-friendly action verbs ("Ticket archived" instead of "unarchive done")
//   2. Consistent description / cta pattern across the app
//   3. A single place to retune toast UX (duration, copy, semantics)
//
// Direct `toast.*` from sonner still works everywhere — this just adds named
// helpers for the patterns we use most. Migrate call sites opportunistically.

import { toast as sonner } from 'sonner';
import type { TicketAction } from './api';

// Past-tense verb form used in success toasts. Reads naturally as
// "Ticket {verb}" — e.g. "Ticket archived", "Ticket marked resolved".
const ACTION_VERB: Record<TicketAction, string> = {
  claim: 'claimed',
  resolve: 'marked resolved',
  escalate: 'escalated',
  reassign: 'reassigned',
  snooze_1h: 'snoozed for 1 hour',
  snooze_4h: 'snoozed for 4 hours',
  snooze_tomorrow: 'snoozed until tomorrow',
  snooze_monday: 'snoozed until Monday',
  snooze_2d: 'snoozed for 2 days',
  snooze_custom: 'snoozed',
  unsnooze: 'woken up',
  merge: 'merged',
  archive: 'archived',
  unarchive: 'restored from archive',
  close: 'closed',
  set_priority: 'priority updated',
  add_watcher: 'watcher added',
  add_note: 'note added',
  reopen: 'reopened',
};

// Present-continuous verbs for the loading toast that fires the
// instant the user clicks an action. Without this, the 2–5 second
// window between click and workflow response feels like nothing
// happened — multiple agents reported the UI "looks broken" because
// they couldn't tell whether their click registered. The loading
// toast appears immediately (sonner spinner), then transitions to
// the success / error toast inline (same toast id, no double-toast).
const ACTION_PROGRESS: Record<TicketAction, string> = {
  claim: 'Claiming ticket…',
  resolve: 'Marking resolved…',
  escalate: 'Escalating…',
  reassign: 'Reassigning…',
  snooze_1h: 'Snoozing for 1 hour…',
  snooze_4h: 'Snoozing for 4 hours…',
  snooze_tomorrow: 'Snoozing until tomorrow…',
  snooze_monday: 'Snoozing until Monday…',
  snooze_2d: 'Snoozing for 2 days…',
  snooze_custom: 'Snoozing…',
  unsnooze: 'Waking up ticket…',
  merge: 'Merging…',
  archive: 'Archiving…',
  unarchive: 'Restoring from archive…',
  close: 'Closing ticket…',
  set_priority: 'Updating priority…',
  add_watcher: 'Adding watcher…',
  add_note: 'Adding note…',
  reopen: 'Reopening…',
};

/** Single-ticket action result. Used by row hover quick-actions, the action
 *  bar on ticket detail, etc. */
export function toastActionDone(action: TicketAction, opts?: { ticketSubject?: string }) {
  const verb = ACTION_VERB[action] ?? action;
  return sonner.success(`Ticket ${verb}`, {
    description: opts?.ticketSubject
      ? truncate(opts.ticketSubject, 80)
      : undefined,
  });
}

export function toastActionFailed(action: TicketAction, error: unknown) {
  return toastError(`Couldn't ${action.replace(/_/g, ' ')} ticket`, error);
}

/** Success toast for an action whose result is ALREADY on screen (the server
 *  hasn't confirmed yet). Carries an id so a refusal can replace it in place
 *  with toastActionRolledBack instead of stacking a second toast. */
export function toastActionInstant(
  action: TicketAction,
  id: string | number,
  opts?: { ticketSubject?: string },
) {
  const base = `Ticket ${ACTION_VERB[action] ?? action}`;
  return sonner.success(
    opts?.ticketSubject ? `${base} — ${truncate(opts.ticketSubject, 60)}` : base,
    { id },
  );
}

/** The server refused (or couldn't be reached) after the screen had already
 *  moved. Replaces the instant toast, holds until dismissed, and offers a
 *  one-click retry. */
export function toastActionRolledBack(
  action: TicketAction,
  id: string | number,
  error: unknown,
  opts?: { onRetry?: () => void },
) {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const friendly = humanizeError(error);
  return sonner.error(`Couldn't ${action.replace(/_/g, ' ')} ticket`, {
    id,
    description:
      (friendly !== raw ? `${friendly}\n\nDetails: ${truncate(raw, 160)}` : truncate(friendly, 200)) +
      '\n\nThe ticket has been put back as it was.',
    duration: Infinity,
    action: opts?.onRetry ? { label: 'Retry', onClick: opts.onRetry } : undefined,
  });
}

/** Wrap a ticket-action mutation's promise so the user sees IMMEDIATE
 *  loading feedback when they click. Single toast slot that morphs
 *  from "Claiming ticket…" (spinner) → "Ticket claimed" (check) or
 *  "Couldn't claim ticket" (red x) when the request resolves.
 *
 *  Replaces the old onSuccess + onError toastActionDone/Failed pattern
 *  at the call site — sonner's promise() handles all three states.
 *  The mutation's onSuccess can still fire for cache invalidation;
 *  just remove the toast call from it.
 *
 *  Returns the promise unchanged so the caller can `await` it if
 *  they want (e.g. to wait for the action before navigating). */
export function toastActionPromise<T>(
  action: TicketAction,
  promise: Promise<T>,
  opts?: { ticketSubject?: string },
): Promise<T> {
  const pastVerb = ACTION_VERB[action] ?? action;
  // sonner.promise's success/error callbacks return a string (or
  // React node). It doesn't support the `{message, description}`
  // object form that the non-promise toasts accept — so we fold
  // any extra context into the message line with an em-dash
  // separator. Reads cleanly in the toast either way.
  sonner.promise(promise, {
    loading: ACTION_PROGRESS[action] ?? `${action.replace(/_/g, ' ')}…`,
    success: () => {
      const base = `Ticket ${pastVerb}`;
      return opts?.ticketSubject ? `${base} — ${truncate(opts.ticketSubject, 60)}` : base;
    },
    error: (e: unknown) => {
      const base = `Couldn't ${action.replace(/_/g, ' ')} ticket`;
      return `${base} — ${truncate(humanizeError(e), 140)}`;
    },
  });
  return promise;
}

/** Bulk action result with success/failure breakdown. */
export function toastBulkActionDone(
  action: TicketAction,
  result: { total: number; failed: number },
  opts?: { id?: string | number },
) {
  const ok = result.total - result.failed;
  const verb = ACTION_VERB[action] ?? action;
  if (result.failed === 0) {
    return sonner.success(
      `${ok} ticket${ok === 1 ? '' : 's'} ${verb}`,
      opts?.id !== undefined ? { id: opts.id } : undefined,
    );
  }
  // The failures stay selected (see BulkActionsBar's `onRetainFailed`), so the
  // retry instruction now points at something the user can actually act on.
  // It also holds until dismissed — a 3.5s toast reporting damage to N client
  // conversations is not enough time to read, let alone act on.
  return sonner.warning(
    `${ok} of ${result.total} ${verb}`,
    {
      id: opts?.id,
      description: `${result.failed} failed and ${result.failed === 1 ? 'is' : 'are'} back as they were, still selected — press the same action again to retry just those.`,
      duration: Infinity,
    },
  );
}

/** Send / save / generic two-line success. */
export function toastSuccess(title: string, description?: string) {
  return sonner.success(title, description ? { description } : undefined);
}

/** Recurring failure shapes, translated into something a support agent can act
 *  on. Everything else falls through to the raw message — a wrong-but-specific
 *  string still beats a vague one. */
export function humanizeError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  if (!raw) return 'Something went wrong.';

  // Gmail per-user rate limit. Must come before the generic 429 branch: this
  // one is NOT "a few seconds" — Google locks the mailbox for ~15 minutes, and
  // every rejected request while locked pushes the unlock time out again, so
  // telling the agent to retry immediately makes it worse.
  if (/User-rate limit exceeded/i.test(raw)) {
    const iso = raw.match(/Retry after (\d{4}-\d{2}-\d{2}T[\d:.]+Z)/i)?.[1];
    const retryAt = iso ? new Date(iso) : null;
    if (retryAt && !Number.isNaN(retryAt.getTime())) {
      const time = retryAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      return `Gmail is temporarily limiting this mailbox. Wait until ${time} before sending — trying sooner can extend the wait.`;
    }
    return 'Gmail is temporarily limiting this mailbox. Wait about 15 minutes before sending — trying sooner can extend the wait.';
  }

  // The dispatcher refused the action (postAction turns its 200-with-ok:false
  // reply into "not_permitted (<reason>)"). Reasons come from authorize_action.
  if (/not_permitted/i.test(raw)) {
    if (/inactive_or_expired/i.test(raw)) {
      return 'Your access to this inbox has ended. Ask an admin if you still need it.';
    }
    if (/assignee_cannot_view/i.test(raw)) {
      return "That teammate can't see this inbox's tickets, so it can't be assigned to them.";
    }
    if (/unknown_ticket/i.test(raw)) {
      return 'That ticket no longer exists. Refresh to see the current list.';
    }
    return "You don't have permission to do that in this inbox. If you think you should, ask an admin to check your access.";
  }

  // Postgres RLS denial. The literal text a user would otherwise read is
  // `new row violates row-level security policy for table "gmail_inboxes"`.
  if (/row-level security|violates row-level security|permission denied/i.test(raw)) {
    return "You don't have permission to do that. If you think you should, ask an admin to check your access.";
  }
  // Unique constraint — almost always "someone already created this".
  if (/duplicate key value|already exists|unique constraint/i.test(raw)) {
    return 'That already exists. Refresh to see the current version.';
  }
  // Foreign key / not-null — a required field or a stale reference.
  if (/violates foreign key|null value in column|not-null constraint/i.test(raw)) {
    return 'Something required is missing or refers to a record that no longer exists. Refresh and try again.';
  }
  // Auth expiry.
  if (/JWT expired|invalid token|not authenticated|401/i.test(raw)) {
    return 'Your session expired. Reload the page to sign in again.';
  }
  // Transport.
  if (/Failed to fetch|NetworkError|ERR_INTERNET|ECONNREFUSED|timeout/i.test(raw)) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  // Upstream capacity — n8n / Anthropic / Supabase all surface these.
  if (/\b(429|502|503|504|529)\b|overloaded|rate.?limit|too many requests/i.test(raw)) {
    return 'The service is briefly busy. Wait a few seconds and try again.';
  }
  // n8n workflow deactivated — a real and recurring failure in this project.
  if (/Active version not found|workflow could not be started/i.test(raw)) {
    return 'The automation that handles this is not running. Ask an admin to re-activate the workflow in n8n.';
  }
  // n8n answered 5xx: the workflow itself errored. The body is JSON noise such
  // as {"message":"Workflow Webhook Error"} that tells the agent nothing.
  if (/^\s*5\d\d\b|Workflow Webhook Error|Internal Server Error/i.test(raw)) {
    return 'The server hit a problem handling that. Try again in a moment — if it keeps happening, tell an admin.';
  }
  return raw;
}

export function toastError(title: string, error?: unknown) {
  const description = error ? humanizeError(error) : undefined;
  const raw = error instanceof Error ? error.message : error ? String(error) : '';
  // Errors hold until dismissed. The global 3500ms default meant a failed
  // reply to a paying client — carrying a raw PostgREST string — was gone
  // before it could be read, let alone acted on. `closeButton` is enabled
  // globally in App.tsx, so there is always a way out.
  return sonner.error(title, {
    description:
      description && description !== raw
        ? // Humanised: append the raw text so it's still reportable.
          `${description}\n\nDetails: ${truncate(raw, 160)}`
        : description
          ? truncate(description, 200)
          : undefined,
    duration: Infinity,
  });
}

/** For the many call sites that had no title at all — they passed the raw
 *  `err.message` as the toast's only text, so the user read a PostgREST string
 *  with no indication of what had failed. Gives them a title and a humanised
 *  body without each site having to invent copy. Prefer `toastError` with a
 *  specific title wherever the action is known. */
export function toastFailure(error: unknown, title = "That didn't work") {
  return toastError(title, error);
}

export function toastInfo(title: string, description?: string) {
  return sonner.info(title, description ? { description } : undefined);
}

/** Validation-style "fix this before continuing" — shorter, no description. */
export function toastValidation(message: string) {
  return sonner.warning(message, { duration: 3000 });
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

// Re-export the raw sonner instance for less-common patterns (loading,
// promise, custom renders, etc).
export { sonner as toast };
